import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Trophy, Users, Globe, Heart, Lightbulb, Share2, Eye } from 'lucide-react';
import { Helmet } from 'react-helmet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComparisonShare = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [comparisonData, setComparisonData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchComparisonData();
  }, [id]);

  const fetchComparisonData = async () => {
    try {
      const response = await axios.get(`${API}/share/comparison/${id}`);
      setComparisonData(response.data);
    } catch (error) {
      console.error('Error fetching comparison:', error);
      setError('Comparison not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallScore = (result) => {
    const valenceScore = (result.valence || 0) * 100;
    const arousalScore = (result.arousal_level || 0) * 100;
    const dominanceScore = (result.dominance || 0) * 100;
    return (valenceScore * 0.5 + arousalScore * 0.3 + dominanceScore * 0.2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading comparison...</p>
        </div>
      </div>
    );
  }

  if (error || !comparisonData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600 mb-4">{error || 'Comparison not found'}</p>
            <Button onClick={() => navigate('/')}>Go to Homepage</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const results = comparisonData.comparison_results.map(r => ({
    ...r,
    overallScore: calculateOverallScore(r)
  }));

  // Get winner image for OG preview
  const winnerImage = results[0]?.preview || results[0]?.image_data;

  return (
    <>
      <Helmet>
        <title>Image Comparison Results | EmoQ.ai</title>
        <meta name="description" content={`Compare ${comparisonData.images_count} images for emotional impact. Winner: Image #${comparisonData.winner_index + 1}${comparisonData.purpose ? ` for ${comparisonData.purpose}` : ''}`} />
        
        {/* Open Graph / Social Media Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={`Image Comparison Results | EmoQ.ai`} />
        <meta property="og:description" content={`${comparisonData.images_count} images compared. Winner: Image #${comparisonData.winner_index + 1}. View detailed emotional analysis.`} />
        <meta property="og:image" content={winnerImage || 'https://customer-assets.emergentagent.com/job_emotionengine/artifacts/i8r1k6ge_Black%20and%20White%20Bold%20Typographic%20Design%20Logo%20-%204.png'} />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Image Comparison Results | EmoQ.ai`} />
        <meta name="twitter:description" content={`${comparisonData.images_count} images compared. Winner: Image #${comparisonData.winner_index + 1}`} />
        <meta name="twitter:image" content={winnerImage || 'https://customer-assets.emergentagent.com/job_emotionengine/artifacts/i8r1k6ge_Black%20and%20White%20Bold%20Typographic%20Design%20Logo%20-%204.png'} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50">
        {/* Header */}
        <header className="border-b border-blue-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-2 md:py-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/3r5buf5n_Emo.zip%20-%204.png"
                alt="EmoQ"
                className="h-14 md:h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-blue-600 to-indigo-600">
              Try EmoQ
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Shared By Banner */}
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Share2 className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Shared by</p>
                    <p className="font-bold text-gray-900">{comparisonData.user_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Eye className="w-4 h-4" />
                  {comparisonData.view_count} views
                </div>
              </div>
              {comparisonData.purpose && (
                <div className="mt-3 p-3 bg-white rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Purpose:</p>
                  <p className="font-medium text-gray-900">{comparisonData.purpose}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Comparison Results ({comparisonData.images_count} Images)
          </h2>

          {/* Winner Card */}
          <Card className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Trophy className="w-12 h-12 text-yellow-600 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-xl font-bold mb-2 text-gray-900">
                    🏆 Winner: Image #{results[0].imageIndex + 1}
                  </h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Overall Score: <span className="font-bold text-lg">{results[0].overallScore.toFixed(1)}/100</span>
                  </p>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <div className="text-xs text-gray-600">Valence</div>
                      <div className="font-bold text-green-600">{((results[0].valence || 0) * 100).toFixed(0)}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Arousal</div>
                      <div className="font-bold text-orange-600">{((results[0].arousal_level || 0) * 100).toFixed(0)}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Dominance</div>
                      <div className="font-bold text-purple-600">{((results[0].dominance || 0) * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                  {results[0].custom_answer && (
                    <div className="p-4 rounded-lg bg-white mt-3 border-l-4 border-yellow-500">
                      <div className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-yellow-600" />
                        Why this image wins:
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {results[0].custom_answer}
                      </p>
                    </div>
                  )}
                </div>
                <img
                  src={results[0].preview || results[0].image_data}
                  alt="Winner"
                  className="w-32 h-32 object-cover rounded-lg border-4 border-yellow-400"
                />
              </div>
            </CardContent>
          </Card>

          {/* Tabbed Detailed Comparison */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Comparison Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="demographics">
                    <Users className="w-4 h-4 mr-1" />
                    Demographics
                  </TabsTrigger>
                  <TabsTrigger value="emotions">
                    <Heart className="w-4 h-4 mr-1" />
                    Emotions
                  </TabsTrigger>
                  <TabsTrigger value="regional">
                    <Globe className="w-4 h-4 mr-1" />
                    Regional
                  </TabsTrigger>
                  <TabsTrigger value="insights">
                    <Lightbulb className="w-4 h-4 mr-1" />
                    Insights
                  </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 text-gray-900">Rank</th>
                          <th className="text-left p-3 text-gray-900">Image</th>
                          <th className="text-left p-3 text-gray-900">Overall Score</th>
                          <th className="text-left p-3 text-gray-900">Emotion</th>
                          <th className="text-left p-3 text-gray-900">Valence</th>
                          <th className="text-left p-3 text-gray-900">Arousal</th>
                          <th className="text-left p-3 text-gray-900">Dominance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((result, index) => (
                          <tr key={result.imageIndex} className="border-b">
                            <td className="p-3">
                              {index === 0 ? (
                                <Badge className="bg-yellow-500">#{index + 1}</Badge>
                              ) : (
                                <Badge variant="secondary">#{index + 1}</Badge>
                              )}
                            </td>
                            <td className="p-3">
                              <img
                                src={result.preview || result.image_data}
                                alt={`Image ${result.imageIndex + 1}`}
                                className="w-16 h-16 object-cover rounded"
                              />
                            </td>
                            <td className="p-3">
                              <span className={`font-bold text-lg ${index === 0 ? 'text-green-600' : 'text-gray-700'}`}>
                                {result.overallScore.toFixed(1)}
                              </span>
                            </td>
                            <td className="p-3">
                              <Badge className="capitalize">{result.dominant_emotion}</Badge>
                            </td>
                            <td className="p-3 text-gray-700">
                              {((result.valence || 0) * 100).toFixed(0)}%
                            </td>
                            <td className="p-3 text-gray-700">
                              {((result.arousal_level || 0) * 100).toFixed(0)}%
                            </td>
                            <td className="p-3 text-gray-700">
                              {((result.dominance || 0) * 100).toFixed(0)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                {/* Demographics Tab */}
                <TabsContent value="demographics" className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Compare how different demographic groups respond to each image
                  </p>
                  {results.map((result, index) => (
                    <div key={result.imageIndex} className="p-4 rounded-lg border bg-white border-gray-200">
                      <div className="flex items-center gap-3 mb-3">
                        <img src={result.preview || result.image_data} alt={`Image ${result.imageIndex + 1}`} className="w-12 h-12 object-cover rounded" />
                        <div>
                          <h5 className="font-bold text-gray-900">
                            Image #{result.imageIndex + 1}
                            {index === 0 && <Badge className="ml-2 bg-yellow-500">Winner</Badge>}
                          </h5>
                          <p className="text-xs text-gray-500">Overall Score: {result.overallScore.toFixed(1)}</p>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-2">
                        {Object.entries(result.audience_reactions || {}).map(([group, reaction]) => (
                          <div key={group} className="p-3 rounded text-sm bg-gray-50">
                            <div className="font-semibold mb-1 text-blue-600">
                              {group}
                            </div>
                            <div className="text-xs text-gray-700">
                              {reaction}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                {/* Emotions Tab */}
                <TabsContent value="emotions" className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Detailed emotion breakdown for each image
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 text-gray-900">Emotion</th>
                          {results.map((result) => (
                            <th key={result.imageIndex} className="text-center p-2 text-gray-900">
                              Image #{result.imageIndex + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(results[0].emotions || {}).map((emotion) => (
                          <tr key={emotion} className="border-b">
                            <td className="p-2 font-medium capitalize text-gray-700">
                              {emotion}
                            </td>
                            {results.map((result) => {
                              const value = result.emotions[emotion] || 0;
                              const maxValue = Math.max(...results.map(r => r.emotions[emotion] || 0));
                              const isMax = value === maxValue && value > 0;
                              return (
                                <td key={result.imageIndex} className="p-2 text-center">
                                  <span className={`font-semibold ${isMax ? 'text-green-600' : 'text-gray-600'}`}>
                                    {Math.round(value)}%
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                {/* Regional Tab */}
                <TabsContent value="regional" className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Regional performance analysis (if available)
                  </p>
                  <div className="p-4 rounded-lg bg-blue-50">
                    <p className="text-sm text-gray-700">
                      💡 Regional data is based on cultural and demographic analysis. The winner (Image #{results[0].imageIndex + 1}) 
                      shows strongest overall emotional resonance across global audiences.
                    </p>
                  </div>
                </TabsContent>

                {/* Insights Tab */}
                <TabsContent value="insights" className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    AI-generated insights and recommendations for each image
                  </p>
                  {results.map((result, index) => (
                    <div key={result.imageIndex} className="p-4 rounded-lg border bg-white border-gray-200">
                      <div className="flex items-center gap-3 mb-3">
                        <img src={result.preview || result.image_data} alt={`Image ${result.imageIndex + 1}`} className="w-12 h-12 object-cover rounded" />
                        <div>
                          <h5 className="font-bold text-gray-900">
                            Image #{result.imageIndex + 1}
                            {index === 0 && <Badge className="ml-2 bg-yellow-500">Winner</Badge>}
                          </h5>
                        </div>
                      </div>
                      
                      {result.custom_answer && (
                        <div className="mb-4 p-3 rounded-lg bg-blue-50 border-l-4 border-blue-500">
                          <div className="font-semibold text-sm mb-2">Comparative Analysis:</div>
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {result.custom_answer}
                          </p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="font-semibold text-sm mb-2">Recommendations:</div>
                        {(result.recommendations || []).map((rec, idx) => (
                          <div key={idx} className="flex gap-2 p-2 rounded text-sm bg-gray-50">
                            <span className="text-green-600 font-bold">{idx + 1}.</span>
                            <span className="text-gray-700">{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="pt-6 text-center">
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Want to compare your own images?
              </h3>
              <p className="text-sm mb-4 text-gray-600">
                Try EmoQ.ai's powerful comparison tool to find which images resonate best with your audience
              </p>
              <Button onClick={() => navigate('/')} size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600">
                Try EmoQ.ai Free
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ComparisonShare;
